import { useState, useRef, useEffect } from "react";
import { Globe, Check, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES, LANGUAGE_LABELS, LANGUAGE_SHORT, setLanguage } from "@/i18n";
import { api } from "@/lib/api";

// Compact language switcher. `variant`: "light" (dark bg) or "dark" (light bg).
export function LanguageSwitcher({ variant = "dark", persist = true, className = "" }) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = (i18n.language || "de").slice(0, 2);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const choose = async (lng) => {
    setLanguage(lng);
    setOpen(false);
    if (persist) { try { await api.put("/me/language", { language: lng }); } catch { /* ignore (guest) */ } }
  };

  const light = variant === "light";
  const triggerCls = light
    ? "border-white/15 bg-white/5 text-fundio-cream hover:bg-white/10"
    : "border-fundio-border bg-white text-fundio-ink hover:bg-fundio-subtle";

  return (
    <div className={`relative ${className}`} ref={ref} data-testid="language-switcher">
      <button type="button" onClick={() => setOpen((s) => !s)} data-testid="language-switcher-trigger"
        className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[12px] font-bold transition-colors ${triggerCls}`}>
        <Globe size={14} /> {LANGUAGE_SHORT[current] || "DE"} <ChevronDown size={13} className="opacity-70" />
      </button>
      {open && (
        <div className="absolute right-0 z-50 mt-2 w-40 overflow-hidden rounded-xl border border-fundio-border bg-white py-1 shadow-lg" data-testid="language-switcher-menu">
          {SUPPORTED_LANGUAGES.map((lng) => (
            <button key={lng} onClick={() => choose(lng)} data-testid={`language-option-${lng}`}
              className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-[13px] text-fundio-ink hover:bg-fundio-subtle">
              <span className="font-mono text-[11px] font-bold text-fundio-slate">{LANGUAGE_SHORT[lng]}</span>
              <span className="flex-1">{LANGUAGE_LABELS[lng]}</span>
              {current === lng && <Check size={15} className="text-fundio-gold" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
